import React, { useContext, useEffect, useMemo } from 'react';
import { api, apiAccessTokenContext } from './api';
import { useSnackbar } from './SnackbarProvider';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import { LoginPage } from '../pages/LoginPage';
import ApplicationBootingPage from '../pages/ApplicationBootingPage';
import { errorHandler } from './ErrorReporting';
import { useFrontendConfiguration } from './FrontendConfigurationProvider';

const APIContext = React.createContext(null);
const UserContext = React.createContext(null);
const IsAuthenticatedContext = React.createContext(null);

const createTransformedAPI = (api, snackbar) => {
  const transformedAPI = {};
  for (let key in api) {
    transformedAPI[key + '_noToastOnError'] = api[key];

    transformedAPI[key] = async (...args) => {
      try {
        return await api[key](...args);
      } catch (err) {
        let message = `Error while making ${key} API call: ${err.message}\n${err.stack}`;
        if (err.toString().includes('Network Error')) {
          message = `Unable to connect to the server. Server may be offline`;
        } else if (err?.response && err?.response?.data) {
          if (err?.response?.data?.message) {
            message = err.response.data.message;
          } else if (err?.response?.data?.error) {
            message = err.response.data.error;
          } else {
            message = JSON.stringify(err.response.data, null, 2);
          }
        } else if (err?.response?.status === 401) {
          message = `You are not authorized to perform this action. Please log in and try again.`;
        } else if (err?.response?.status === 403) {
          message = `You are not authorized to perform this action. Please contact your administrator for assistance.`;
        } else if (err?.response?.status === 404) {
          message = `The requested resource was not found. Please refresh the page or log out and back in.`;
        }

        snackbar.toast({
          message: message,
          severity: 'error',
          autoHideMs: 15000,
        });

        // Swallow the error and ignore it
        throw err;
      }
    };
  }
  return transformedAPI;
};

/**
 * The API provider is a wrapper around the API that can display error
 * messages in a UI Snackbar if any occur. This allows all components
 * to use the API while getting a basic level of feedback on errors
 * without explicitly handling those errors
 *
 * @param children
 * @returns {Element}
 * @constructor
 */
export const APIProvider = ({ children }) => {
  const config = useFrontendConfiguration();

  const { user, isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();

  let injectedToken = null;
  // Check localStorage for an injected-token field, which is used by automated tests to
  // inject a token into the frontend so that test code can be run.
  if (window.location.search.includes('injected-token=')) {
    // Check the URL for an injected-token query parameter, which is used by automated tests to
    // inject a token into the frontend so that test code can be run.
    const url = new URL(window.location.href);
    injectedToken = url.searchParams.get('injected-token');
    axios.defaults.headers.common['WWW-Authenticate'] = injectedToken;
    // Change the query to remove injected token but keep everything else
    url.searchParams.delete('injected-token');
  }

  const SSO_TOKEN_STORAGE_KEY = 'prospera_sso_access_token';

  const url = new URL(window.location.href);
  const urlAccessToken = url.searchParams.get('access_token');
  // Also check sessionStorage so the token survives third-party OAuth redirects
  // (e.g. Google/Outlook OAuth) that cause a full page reload and destroy React state.
  const storedSsoToken = sessionStorage.getItem(SSO_TOKEN_STORAGE_KEY);
  const ssoAccessToken =
    !isLoading && !isAuthenticated ? (urlAccessToken || storedSsoToken) : null;
  const ssoTokenUsedRef = React.useRef(null);
  const urlToken = injectedToken || ssoAccessToken || ssoTokenUsedRef.current;

  const [apiAccessToken, setApiAccessToken] = React.useState(injectedToken || ssoAccessToken);
  const [urlTokenUser, setUrlTokenUser] = React.useState(null);

  const snackbar = useSnackbar();
  const transformedAPI = useMemo(
    () => createTransformedAPI(api, snackbar),
    [snackbar]
  );

  useEffect(() => {
    const getAccessToken = async () => {
      try {
        const options = {
          authorizationParams: {
            audience: config.frontend.auth0_audience,
          },
        };

        const accessToken = await getAccessTokenSilently(options);

        setApiAccessToken(accessToken);
        axios.defaults.headers.common['WWW-Authenticate'] = accessToken;
        if (errorHandler) {
          errorHandler.setUser(user?.email);
        }
      } catch (e) {
        console.log('Error during login', e);
        snackbar.toast({
          message: e.toString(),
          severity: 'error',
          autoHideMs: 15000,
        });
      }
    };

    if (!apiAccessToken && urlToken) {
      axios.defaults.headers.common['WWW-Authenticate'] = urlToken;
      setApiAccessToken(urlToken);
      if (ssoAccessToken) {
        ssoTokenUsedRef.current = ssoAccessToken;
        // Persist to sessionStorage so the token survives full-page OAuth redirects
        // (Google/Outlook OAuth) and is available when the app re-boots after the
        // provider's callback redirects back here.
        sessionStorage.setItem(SSO_TOKEN_STORAGE_KEY, ssoAccessToken);
        if (urlAccessToken) {
          // Only clean the URL when the token came from the URL (not from storage)
          const cleanUrl = new URL(window.location.href);
          cleanUrl.searchParams.delete('access_token');
          window.history.replaceState({}, '', cleanUrl.pathname + cleanUrl.search);
        }
      }
    } else if (isAuthenticated && !apiAccessToken) {
      // User authenticated via Auth0 — clear any stored SSO token so it doesn't
      // interfere with future sessions.
      sessionStorage.removeItem(SSO_TOKEN_STORAGE_KEY);
      const error_description = new URLSearchParams(window.location.search).get(
        'error_description'
      );
      if (error_description) {
        // If there is an error field in the URL, they we need
        // to display that to the user. grab the error_description
        // field from the query description
        snackbar.toast({
          message:
            error_description || 'An unknown error occurred during login.',
          severity: 'error',
          autoHideMs: 15000,
        });
      } else {
        getAccessToken().then(() => {
          let redirectPath = '';
          if (localStorage.getItem('prospera-login-redirect-path')) {
            redirectPath = localStorage.getItem('prospera-login-redirect-path');
            localStorage.removeItem('prospera-login-redirect-path');
          }
          if (redirectPath && window.location.pathname !== redirectPath) {
            window.location.pathname = redirectPath;
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAuthenticated,
    isLoading,
    getAccessTokenSilently,
    user?.sub,
    user?.email,
    apiAccessToken,
    snackbar,
    injectedToken,
    urlToken,
    urlAccessToken,
  ]);

  useEffect(() => {
    if (!urlToken || !apiAccessToken) return;
    axios
      .get('/user/me')
      .then((response) => setUrlTokenUser(response.data))
      .catch(() => setUrlTokenUser({}));
  }, [urlToken, apiAccessToken]);

  if (urlToken) {
    if (!apiAccessToken) {
      return (
        <ApplicationBootingPage
          description={'Getting API token for injected token...'}
        />
      );
    }
  } else if (isLoading || storedSsoToken) {
    // Auth0 is still determining auth status, or there is a stored SSO token
    // in sessionStorage that the effect will pick up once isLoading resolves.
    // Never show <LoginPage /> here — its 750ms loginWithRedirect() timer would
    // fire and redirect to Auth0 before we can restore the SSO session.
    return (
      <ApplicationBootingPage
        description={'Checking authentication...'}
      />
    );
  } else if (!isAuthenticated) {
    return <LoginPage />;
  } else if (isAuthenticated && !apiAccessToken) {
    return (
      <ApplicationBootingPage
        description={'Getting API token for your user...'}
      />
    );
  }

  return (
    <apiAccessTokenContext.Provider value={apiAccessToken || urlToken}>
      <APIContext.Provider value={transformedAPI}>
        <IsAuthenticatedContext.Provider
          value={urlToken ? true : isAuthenticated}
        >
          <UserContext.Provider value={urlToken ? (urlTokenUser ?? {}) : user}>
            {children}
          </UserContext.Provider>
        </IsAuthenticatedContext.Provider>
      </APIContext.Provider>
    </apiAccessTokenContext.Provider>
  );
};

export const useUser = useContext.bind(null, UserContext);
export const useIsAuthenticated = useContext.bind(null, IsAuthenticatedContext);
export const useAPI = useContext.bind(null, APIContext);
